import { captureDocumentScrollRatio } from '@/lib/document-scroll-restore'
import { restoreDocumentScrollRatio } from '@/lib/document-scroll-restore'
import {
  clearSoftBookmark,
  getSoftBookmark,
  headingRatiosFromOffsets,
  nearestHeadingIdForRatio,
  setSoftBookmark,
} from '@/lib/document-soft-bookmark'
import { computeHeadingRatios, extractHeadings } from '@/lib/heading-utils'

export const JUMP_SOFT_BOOKMARK_EVENT = 'flownotes-jump-soft-bookmark'

export type JumpSoftBookmarkDetail = {
  documentId: string
}

function findScrollRoot(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>(
      '[data-testid="markdown-editor-wysiwyg-preview"][data-scroll-container]'
    ) ||
    document.querySelector<HTMLElement>(
      '[data-testid="markdown-editor-wysiwyg-edit"][data-scroll-container]'
    ) ||
    document.querySelector<HTMLElement>('[data-scroll-container="true"]')
  )
}

function collectRenderedHeadingOffsets(root: HTMLElement): Array<{ id: string; offsetTop: number }> {
  const contentRoot =
    (root.querySelector('.document-reading-prose') as HTMLElement | null) || root
  const nodes = contentRoot.querySelectorAll('h1[id], h2[id], h3[id], h4[id]')
  const headings: Array<{ id: string; offsetTop: number }> = []
  nodes.forEach((node) => {
    const el = node as HTMLElement
    if (el.id) headings.push({ id: el.id, offsetTop: el.offsetTop })
  })
  return headings
}

/** Bookmark current viewport for the active document. */
export function bookmarkCurrentReadingPosition(
  documentId: string,
  markdown?: string
): void {
  const scrollRoot = findScrollRoot()
  const ratio = captureDocumentScrollRatio(scrollRoot) ?? 0
  let headingId: string | null = null
  if (scrollRoot) {
    const rendered = collectRenderedHeadingOffsets(scrollRoot)
    if (rendered.length > 0) {
      headingId = nearestHeadingIdForRatio(
        headingRatiosFromOffsets(rendered, scrollRoot.scrollHeight),
        ratio
      )
    } else if (markdown) {
      const contentRoot =
        (scrollRoot.querySelector('.document-reading-prose') as HTMLElement | null) ||
        scrollRoot
      const headings = computeHeadingRatios(
        extractHeadings(markdown),
        contentRoot,
        scrollRoot
      )
      headingId = nearestHeadingIdForRatio(headings, ratio)
    }
  }
  setSoftBookmark(documentId, { ratio, headingId })
}

export function jumpToSoftBookmark(documentId: string): boolean {
  const bookmark = getSoftBookmark(documentId)
  if (!bookmark) return false
  const scrollRoot = findScrollRoot()
  if (!scrollRoot) {
    window.dispatchEvent(
      new CustomEvent(JUMP_SOFT_BOOKMARK_EVENT, {
        detail: { documentId } satisfies JumpSoftBookmarkDetail,
      })
    )
    return true
  }
  if (bookmark.headingId) {
    const el = scrollRoot.querySelector(`[id="${CSS.escape(bookmark.headingId)}"]`)
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return true
    }
  }
  restoreDocumentScrollRatio(scrollRoot, bookmark.ratio)
  return true
}

export function clearDocumentSoftBookmark(documentId: string): void {
  clearSoftBookmark(documentId)
}
